import { useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@pazportal/ui';
import type { Json } from '@pazportal/supabase';
import { useActiveTrip, type ActiveTrip } from '../hooks/useActiveTrip';
import {
  describeIntegrationState,
  formatAvailabilityPct,
  useIntegrationStatus,
  useLocationDevices,
  useLocationNetworkLive,
  useLocationUnifiSites,
  type LocationDevice,
  type UnifiSiteSummary,
} from '../hooks/useLocationDevices';
import { networkDocumentsQueryKey, saveNetworkDocument, useNetworkDocuments, type NetworkDocument } from '../hooks/useNetworkDocuments';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toaster';
import { TabPanel, TabStrip } from '../components/Tabs';
import { ErrorBlock, LoadingBlock, NoActiveTrip } from '../components/PageState';
import { TripPicker } from '../components/TripPicker';
import { friendlyDbError, isOnline } from '../lib/errors';
import { EM_DASH, formatDateTime, pluralize, timeAgo } from '../lib/formatters';
import {
  EMPTY_TOPOLOGY,
  NETWORK_DOC_TITLES,
  SSID_FIELDS,
  SSID_SECURITY_OPTIONS,
  TOPOLOGY_FIELDS,
  VLAN_FIELDS,
  emptySsidRow,
  emptyVlanRow,
  parseSsids,
  parseTopology,
  parseVlans,
  serializeSsids,
  serializeTopology,
  serializeVlans,
  type NetworkDocKind,
  type SsidRow,
  type TopologyForm,
  type VlanRow,
} from '../lib/networkDocs';

type Tab = 'topology' | 'devices' | 'vlans' | 'wireless';

const TABS: { key: Tab; label: string }[] = [
  { key: 'topology', label: 'Topology' },
  { key: 'devices', label: 'Devices' },
  { key: 'vlans', label: 'VLANs' },
  { key: 'wireless', label: 'Wireless' },
];

export function NetworkPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const [tab, setTab] = useState<Tab>('topology');
  const prefix = useId();

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-4">
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-text">Network documentation</h1>
        <p className="mt-0.5 truncate text-sm text-text-muted">{trip.location?.name ?? trip.customer?.name ?? trip.project_number}</p>
      </div>
      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}

      <TabStrip tabs={TABS} value={tab} onChange={setTab} label="Network documentation sections" idPrefix={prefix} />

      <TabPanel prefix={prefix} tabKey={tab}>
        {tab === 'topology' && <TopologyTab trip={trip} />}
        {tab === 'devices' && <DevicesTab locationId={trip.location?.id} />}
        {tab === 'vlans' && <VlanTab trip={trip} />}
        {tab === 'wireless' && <WirelessTab trip={trip} />}
      </TabPanel>
    </div>
  );
}

// ─── Shared save path for the three structured notes ─────────────────────────

function useSaveNetworkDoc(trip: ActiveTrip, kind: NetworkDocKind) {
  const { profileId } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const title = NETWORK_DOC_TITLES[kind];

  const save = async (metadata: Json): Promise<boolean> => {
    const locationId = trip.location?.id;
    const customerId = trip.customer?.id;
    if (!locationId || !customerId) {
      notify('This trip has no customer or location on record, so notes cannot be saved.', 'error');
      return false;
    }
    if (!isOnline()) {
      notify(`You are offline. ${title} is not queued — reconnect and save again.`, 'error');
      return false;
    }
    setSaving(true);
    try {
      await saveNetworkDocument({ kind, locationId, tripId: trip.id, customerId, uploadedByUserId: profileId, metadata });
      notify(`${title} saved.`, 'success');
      await qc.invalidateQueries({ queryKey: networkDocumentsQueryKey(locationId) });
      return true;
    } catch (e) {
      notify(`Could not save ${title.toLowerCase()}: ${friendlyDbError(e)}`, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
}

function SavedLine({ doc, dirty }: { doc: NetworkDocument | null; dirty: boolean }) {
  return (
    <p className="text-xs text-text-muted" aria-live="polite">
      {dirty ? 'Unsaved changes. ' : ''}
      {doc ? `Last saved ${formatDateTime(doc.updated_at)}.` : 'Not saved yet.'}
    </p>
  );
}

// ─── Topology ────────────────────────────────────────────────────────────────

function TopologyTab({ trip }: { trip: ActiveTrip }) {
  const docs = useNetworkDocuments(trip.location?.id);
  if (docs.isLoading) return <LoadingBlock label="Loading topology notes…" />;
  if (docs.isError) return <ErrorBlock title="Could not load topology notes" error={docs.error} onRetry={() => void docs.refetch()} />;
  const doc = docs.data?.topology ?? null;
  return <TopologyEditor key={doc?.id ?? 'new'} trip={trip} doc={doc} />;
}

function TopologyEditor({ trip, doc }: { trip: ActiveTrip; doc: NetworkDocument | null }) {
  const [form, setForm] = useState<TopologyForm>(() => (doc ? parseTopology(doc.metadata) : { ...EMPTY_TOPOLOGY }));
  const { save, saving } = useSaveNetworkDoc(trip, 'topology');
  const idPrefix = useId();
  const stored = JSON.stringify(serializeTopology(doc ? parseTopology(doc.metadata) : EMPTY_TOPOLOGY));
  const dirty = JSON.stringify(serializeTopology(form)) !== stored;

  const update = (key: keyof TopologyForm, value: string) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <form
      className="card space-y-4 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save(serializeTopology(form));
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {TOPOLOGY_FIELDS.map((f) => {
          const id = `${idPrefix}-${f.key}`;
          return (
            <div key={f.key}>
              <label htmlFor={id} className="input-label">
                {f.label}
                {f.hint && <span className="font-normal text-text-faint"> ({f.hint})</span>}
              </label>
              <input id={id} value={form[f.key]} onChange={(e) => update(f.key, e.target.value)} className="input-field" inputMode={f.inputMode} autoComplete="off" />
            </div>
          );
        })}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-architecture_notes`} className="input-label">
          Architecture notes
        </label>
        <textarea
          id={`${idPrefix}-architecture_notes`}
          value={form.architecture_notes}
          onChange={(e) => update('architecture_notes', e.target.value)}
          rows={4}
          className="input-field"
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <SavedLine doc={doc} dirty={dirty} />
        <button type="submit" disabled={saving} className="btn-primary sm:min-w-[10rem]">
          {saving ? 'Saving…' : 'Save topology'}
        </button>
      </div>
    </form>
  );
}

// ─── Row editors (VLANs / SSIDs) ─────────────────────────────────────────────

interface RowField<T> {
  key: keyof T & string;
  label: string;
  options?: string[];
  inputMode?: 'numeric';
}

function RowsEditor<T extends { id: string }>({
  rows,
  fields,
  rowNoun,
  onChange,
  onRemove,
  onAdd,
}: {
  rows: T[];
  fields: RowField<T>[];
  rowNoun: string;
  onChange: (id: string, key: keyof T & string, value: string) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
}) {
  const isDesktop = useIsDesktop();
  const idPrefix = useId();

  const control = (row: T, field: RowField<T>, index: number, labelled: boolean) => {
    const id = `${idPrefix}-${row.id}-${field.key}`;
    const value = String(row[field.key] ?? '');
    const aria = labelled ? undefined : `${field.label}, ${rowNoun} ${index + 1}`;
    if (field.options) {
      return (
        <select id={id} value={value} onChange={(e) => onChange(row.id, field.key, e.target.value)} className="input-field" aria-label={aria}>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input id={id} value={value} onChange={(e) => onChange(row.id, field.key, e.target.value)} className="input-field" inputMode={field.inputMode} autoComplete="off" aria-label={aria} />
    );
  };

  const removeButton = (row: T, index: number) => (
    <button
      type="button"
      onClick={() => onRemove(row.id)}
      className="btn-danger px-3"
      aria-label={`Remove ${rowNoun} ${index + 1}`}
      disabled={rows.length === 1 && fields.every((f) => String(row[f.key] ?? '').trim() === '' || Boolean(f.options))}
    >
      Remove
    </button>
  );

  return (
    <div className="space-y-3">
      {isDesktop ? (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                {fields.map((f) => (
                  <th key={f.key} scope="col">
                    {f.label}
                  </th>
                ))}
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  {fields.map((f) => (
                    <td key={f.key} className="min-w-[8rem]">
                      {control(row, f, index, false)}
                    </td>
                  ))}
                  <td className="w-24">{removeButton(row, index)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="data-cards">
          {rows.map((row, index) => (
            <li key={row.id} className="data-card space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-text">
                  {rowNoun} {index + 1}
                </p>
                {removeButton(row, index)}
              </div>
              {fields.map((f) => (
                <div key={f.key}>
                  <label htmlFor={`${idPrefix}-${row.id}-${f.key}`} className="input-label">
                    {f.label}
                  </label>
                  {control(row, f, index, true)}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={onAdd} className="btn-secondary">
        Add {rowNoun.toLowerCase()}
      </button>
    </div>
  );
}

function updateRow<T extends { id: string }>(rows: T[], id: string, key: keyof T & string, value: string): T[] {
  return rows.map((r) => (r.id === id ? { ...r, [key]: value } : r));
}

// ─── VLANs ───────────────────────────────────────────────────────────────────

function VlanTab({ trip }: { trip: ActiveTrip }) {
  const docs = useNetworkDocuments(trip.location?.id);
  if (docs.isLoading) return <LoadingBlock label="Loading VLAN notes…" />;
  if (docs.isError) return <ErrorBlock title="Could not load VLAN notes" error={docs.error} onRetry={() => void docs.refetch()} />;
  const doc = docs.data?.vlans ?? null;
  return <VlanEditor key={doc?.id ?? 'new'} trip={trip} doc={doc} />;
}

function VlanEditor({ trip, doc }: { trip: ActiveTrip; doc: NetworkDocument | null }) {
  const [rows, setRows] = useState<VlanRow[]>(() => {
    const parsed = doc ? parseVlans(doc.metadata) : [];
    return parsed.length > 0 ? parsed : [emptyVlanRow()];
  });
  const { save, saving } = useSaveNetworkDoc(trip, 'vlans');
  const stored = JSON.stringify(doc ? serializeVlans(parseVlans(doc.metadata)) : serializeVlans([]));
  const dirty = JSON.stringify(serializeVlans(rows)) !== stored;

  return (
    <form
      className="card space-y-4 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save(serializeVlans(rows));
      }}
    >
      <p className="text-xs text-text-muted">Field notes captured on site. Empty rows are dropped on save.</p>
      <RowsEditor
        rows={rows}
        fields={VLAN_FIELDS}
        rowNoun="VLAN"
        onChange={(id, key, value) => setRows((r) => updateRow(r, id, key, value))}
        onRemove={(id) => setRows((r) => (r.length === 1 ? [emptyVlanRow()] : r.filter((row) => row.id !== id)))}
        onAdd={() => setRows((r) => [...r, emptyVlanRow()])}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <SavedLine doc={doc} dirty={dirty} />
        <button type="submit" disabled={saving} className="btn-primary sm:min-w-[10rem]">
          {saving ? 'Saving…' : 'Save VLANs'}
        </button>
      </div>
    </form>
  );
}

// ─── Wireless ────────────────────────────────────────────────────────────────

function WirelessTab({ trip }: { trip: ActiveTrip }) {
  const locationId = trip.location?.id;
  const docs = useNetworkDocuments(locationId);
  const live = useLocationNetworkLive(locationId);
  const unifi = useLocationUnifiSites(locationId);
  const integration = useIntegrationStatus();

  const wlans = live.data?.wlans ?? [];
  const clients = live.data?.clients ?? [];
  const accessPoints = live.data?.accessPoints ?? [];
  const sites = unifi.data ?? [];
  const ssidCount = sites.reduce<number | null>((acc, s) => (s.ssid_count === null ? acc : (acc ?? 0) + s.ssid_count), null);
  const unifiState = integration.data?.unifi.state;

  return (
    <div className="space-y-4">
      <section className="card space-y-2 p-4" aria-labelledby="unifi-wireless">
        <h2 id="unifi-wireless" className="text-sm font-semibold text-text">
          UniFi Site Manager
        </h2>
        {unifi.isLoading || live.isLoading ? (
          <p className="text-sm text-text-muted">Loading UniFi data…</p>
        ) : unifi.isError ? (
          <ErrorBlock title="Could not load UniFi sites" error={unifi.error} onRetry={() => void unifi.refetch()} />
        ) : live.isError ? (
          <ErrorBlock title="Could not load wireless details" error={live.error} onRetry={() => void live.refetch()} />
        ) : sites.length === 0 ? (
          <p className="text-sm text-text-muted">
            No UniFi site is mapped to this location. {describeIntegrationState('UniFi Site Manager', unifiState)}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric label="SSIDs" value={ssidCount === null ? EM_DASH : String(ssidCount)} />
              <Metric label="Wi-Fi clients" value={sumOrDash(sites.map((s) => s.wifi_client_count))} />
              <Metric label="Wired clients" value={sumOrDash(sites.map((s) => s.wired_client_count))} />
              <Metric label="Updated" value={sites[0]?.updated_at ? timeAgo(sites[0].updated_at) : EM_DASH} />
            </div>
            <p className="text-xs text-text-muted">
              {pluralize(sites.length, 'mapped site')}: {sites.map((s) => s.site_name ?? s.unifi_host_name ?? 'Unnamed').join(', ')}
            </p>
          </>
        )}
      </section>

      {!live.isLoading && !live.isError && (
        <>
          <ListCard
            title="Live SSIDs"
            emptyText={sites.length === 0 ? 'No SSID detail is available until this site is mapped in UniFi.' : 'Per-SSID detail has not been synced for this site yet.'}
            items={wlans.map((w) => ({
              id: w.id,
              primary: w.name ?? 'Unnamed SSID',
              secondary: [w.security, w.enabled === false ? 'disabled' : w.enabled === true ? 'enabled' : null, w.hidden ? 'hidden' : null].filter(Boolean).join(' · '),
            }))}
          />
          <ListCard
            title="Connected clients"
            emptyText="No per-client detail has been synced for this site."
            items={clients.map((c) => ({
              id: c.id,
              primary: c.name ?? c.mac_address ?? 'Client',
              secondary: [c.ssid, c.connection_type, c.ip_address].filter(Boolean).join(' · '),
            }))}
          />
          <ListCard
            title="Access points"
            emptyText="No access-point detail has been synced for this site."
            items={accessPoints.map((ap) => ({
              id: ap.id,
              primary: ap.name ?? 'Access point',
              secondary: [ap.model, ap.mac_address, ap.firmware_version ? `fw ${ap.firmware_version}` : null].filter(Boolean).join(' · '),
            }))}
          />
        </>
      )}

      {docs.isLoading ? (
        <LoadingBlock label="Loading wireless notes…" />
      ) : docs.isError ? (
        <ErrorBlock title="Could not load wireless notes" error={docs.error} onRetry={() => void docs.refetch()} />
      ) : (
        <SsidEditor key={docs.data?.wireless?.id ?? 'new'} trip={trip} doc={docs.data?.wireless ?? null} />
      )}
    </div>
  );
}

function SsidEditor({ trip, doc }: { trip: ActiveTrip; doc: NetworkDocument | null }) {
  const [rows, setRows] = useState<SsidRow[]>(() => {
    const parsed = doc ? parseSsids(doc.metadata) : [];
    return parsed.length > 0 ? parsed : [emptySsidRow()];
  });
  const { save, saving } = useSaveNetworkDoc(trip, 'wireless');
  const stored = JSON.stringify(doc ? serializeSsids(parseSsids(doc.metadata)) : serializeSsids([]));
  const dirty = JSON.stringify(serializeSsids(rows)) !== stored;

  const fields: RowField<SsidRow>[] = [
    { key: 'ssid', label: 'SSID' },
    { key: 'security', label: 'Security', options: SSID_SECURITY_OPTIONS },
    ...SSID_FIELDS.filter((f) => f.key !== 'ssid').map((f) => ({ key: f.key, label: f.label })),
  ];

  return (
    <form
      className="card space-y-4 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save(serializeSsids(rows));
      }}
    >
      <div>
        <h2 className="text-sm font-semibold text-text">Wireless field notes</h2>
        <p className="text-xs text-text-muted">Planned or verified SSID configuration recorded by the crew; not a live UniFi feed.</p>
      </div>
      <RowsEditor
        rows={rows}
        fields={fields}
        rowNoun="SSID"
        onChange={(id, key, value) => setRows((r) => updateRow(r, id, key, value))}
        onRemove={(id) => setRows((r) => (r.length === 1 ? [emptySsidRow()] : r.filter((row) => row.id !== id)))}
        onAdd={() => setRows((r) => [...r, emptySsidRow()])}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <SavedLine doc={doc} dirty={dirty} />
        <button type="submit" disabled={saving} className="btn-primary sm:min-w-[10rem]">
          {saving ? 'Saving…' : 'Save wireless notes'}
        </button>
      </div>
    </form>
  );
}

// ─── Devices (Auvik + UniFi counts) ──────────────────────────────────────────

function DevicesTab({ locationId }: { locationId: string | undefined }) {
  const devices = useLocationDevices(locationId);
  const live = useLocationNetworkLive(locationId);
  const unifi = useLocationUnifiSites(locationId);
  const integration = useIntegrationStatus();
  const isDesktop = useIsDesktop();

  const auvikState = integration.data?.auvik.state;
  const unifiState = integration.data?.unifi.state;
  const list = devices.data ?? [];
  const alerts = live.data?.alerts ?? [];
  const interfaces = live.data?.interfaces ?? [];

  const auvikEmptyText =
    auvikState === 'not_configured'
      ? 'Auvik is not connected, so live device status, alerts and interfaces are unavailable for this site.'
      : `No Auvik devices are mapped to this location. ${describeIntegrationState('Auvik', auvikState)}`;

  return (
    <div className="space-y-4">
      <section className="card space-y-3 p-4" aria-labelledby="unifi-site">
        <h2 id="unifi-site" className="text-sm font-semibold text-text">
          UniFi Site Manager
        </h2>
        {unifi.isLoading ? (
          <p className="text-sm text-text-muted">Loading UniFi sites…</p>
        ) : unifi.isError ? (
          <ErrorBlock title="Could not load UniFi sites" error={unifi.error} onRetry={() => void unifi.refetch()} />
        ) : (unifi.data ?? []).length === 0 ? (
          <p className="text-sm text-text-muted">
            No UniFi site is mapped to this location. {describeIntegrationState('UniFi Site Manager', unifiState)}
          </p>
        ) : (
          (unifi.data ?? []).map((site) => <UnifiSiteCard key={site.id ?? site.site_name ?? 'site'} site={site} />)
        )}
      </section>

      <section className="card p-4" aria-labelledby="auvik-devices">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 id="auvik-devices" className="text-sm font-semibold text-text">
              Auvik devices
            </h2>
            <p className="text-xs text-text-muted">Live status from Auvik. Commissioned inventory is on Inventory and Lifecycle.</p>
          </div>
          {integration.data && auvikState && <StatusBadge status={auvikState === 'not_configured' ? 'unconfigured' : auvikState} />}
        </div>
        {devices.isLoading ? (
          <LoadingBlock label="Loading devices…" />
        ) : devices.isError ? (
          <ErrorBlock title="Could not load devices" error={devices.error} onRetry={() => void devices.refetch()} />
        ) : list.length === 0 ? (
          <p className="text-sm text-text-muted">{auvikEmptyText}</p>
        ) : isDesktop ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Device</th>
                  <th scope="col">Type</th>
                  <th scope="col">IP</th>
                  <th scope="col">Availability</th>
                  <th scope="col">Firmware</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <p>{d.name ?? EM_DASH}</p>
                      <p className="text-xs text-text-muted">{[d.vendor_name, d.make_model].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td>{d.device_type ?? EM_DASH}</td>
                    <td className="font-mono text-xs">{d.ip_address ?? EM_DASH}</td>
                    <td>{formatAvailabilityPct(d.availability_pct)}</td>
                    <td className="text-xs">{d.firmware_version ?? EM_DASH}</td>
                    <td>{d.status ? <StatusBadge status={d.status} /> : EM_DASH}</td>
                    <td className="text-xs text-text-muted">{d.last_seen_at ? formatDateTime(d.last_seen_at) : EM_DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <ul className="data-cards">
            {list.map((d) => (
              <DeviceCard key={d.id} device={d} />
            ))}
          </ul>
        )}
      </section>

      {!live.isLoading && !live.isError && (list.length > 0 || alerts.length > 0 || interfaces.length > 0) && (
        <>
          <ListCard
            title="Open alerts (Auvik)"
            emptyText="No open alerts."
            items={alerts.map((a) => ({
              id: a.id,
              primary: a.alert_name ?? 'Alert',
              secondary: [a.entity_name, a.message].filter(Boolean).join(' · '),
              badge: a.severity,
            }))}
          />
          <ListCard
            title="Interfaces (Auvik)"
            emptyText="No interfaces synced."
            items={interfaces.slice(0, 40).map((i) => ({
              id: i.id,
              primary: i.name ?? 'Interface',
              secondary: [i.interface_type, i.ip_address, i.negotiated_speed].filter(Boolean).join(' · '),
              badge: i.operational_status,
            }))}
          />
        </>
      )}
      {live.isError && <ErrorBlock title="Could not load alerts and interfaces" error={live.error} onRetry={() => void live.refetch()} />}
    </div>
  );
}

function DeviceCard({ device: d }: { device: LocationDevice }) {
  return (
    <li className="data-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">{d.name ?? EM_DASH}</p>
          <p className="truncate text-xs text-text-muted">{[d.vendor_name, d.make_model, d.device_type].filter(Boolean).join(' · ') || EM_DASH}</p>
        </div>
        {d.status ? <StatusBadge status={d.status} /> : <span className="text-xs text-text-muted">No status</span>}
      </div>
      <dl>
        {d.ip_address && (
          <>
            <dt>IP</dt>
            <dd className="font-mono">{d.ip_address}</dd>
          </>
        )}
        <dt>Availability</dt>
        <dd>{formatAvailabilityPct(d.availability_pct)}</dd>
        <dt>Firmware</dt>
        <dd>{d.firmware_version ?? EM_DASH}</dd>
        <dt>Last seen</dt>
        <dd>{d.last_seen_at ? formatDateTime(d.last_seen_at) : EM_DASH}</dd>
      </dl>
    </li>
  );
}

function UnifiSiteCard({ site }: { site: UnifiSiteSummary }) {
  const n = (v: number | null) => (v === null ? EM_DASH : String(v));
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">{site.site_name ?? 'Unnamed site'}</p>
          {site.unifi_host_name && <p className="truncate text-xs text-text-muted">{site.unifi_host_name}</p>}
        </div>
        <p className="shrink-0 text-xs text-text-muted">{site.updated_at ? `Updated ${timeAgo(site.updated_at)}` : 'No update time'}</p>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Devices" value={n(site.total_devices)} />
        <Metric label="Offline" value={n(site.offline_devices)} tone={site.offline_devices ? 'error' : undefined} />
        <Metric label="Wi-Fi clients" value={n(site.wifi_client_count)} />
        <Metric label="Wired clients" value={n(site.wired_client_count)} />
        <Metric label="SSIDs" value={n(site.ssid_count)} />
        <Metric label="WAN links" value={n(site.wan_links)} />
        <Metric label="Pending updates" value={n(site.pending_update_devices)} tone={site.pending_update_devices ? 'warning' : undefined} />
        <Metric label="Critical alerts" value={n(site.critical_notifications)} tone={site.critical_notifications ? 'error' : undefined} />
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'error' | 'warning' }) {
  const color = tone === 'error' ? 'text-error' : tone === 'warning' ? 'text-warning' : 'text-text';
  return (
    <div className="rounded-md bg-surface-2 px-3 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <p className={`text-base font-bold ${color}`}>{value}</p>
    </div>
  );
}

function sumOrDash(values: (number | null)[]): string {
  const known = values.filter((v): v is number => v !== null);
  return known.length === 0 ? EM_DASH : String(known.reduce((a, b) => a + b, 0));
}

function ListCard({
  title,
  emptyText,
  items,
}: {
  title: string;
  emptyText: string;
  items: { id: string; primary: string; secondary: string; badge?: string | null }[];
}) {
  return (
    <section className="card p-4">
      <h2 className="mb-2 text-sm font-semibold text-text">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="text-sm text-text">{item.primary}</p>
                {item.secondary && <p className="break-words text-xs text-text-muted">{item.secondary}</p>}
              </div>
              {item.badge ? <StatusBadge status={item.badge} /> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
